import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

/**
 * ProtectedRoute
 *   <ProtectedRoute role="school_owner">...</ProtectedRoute>
 *   <ProtectedRoute role={["school_owner", "branch_admin"]} permission="tuition_view">...</ProtectedRoute>
 *
 * - `role` may be a single role string or an array of allowed roles.
 * - `permission` (optional) is a single permission key. school_owners always
 *    pass; branch_admins must have that flag set to true.
 */
export default function ProtectedRoute({ children, role, permission }) {
  const { user, checking } = useAuth();

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="auth-loading">
        <div className="text-brand-purple font-heading text-xl animate-pulse">Loading...</div>
      </div>
    );
  }

  if (!user) return <Navigate to="/" replace />;

  const roles = Array.isArray(role) ? role : (role ? [role] : null);
  if (roles && !roles.includes(user.role)) {
    let target = "/dashboard";
    if (user.role === "super_admin") target = "/admin";
    else if (user.role === "parent") target = user.must_change_password ? "/parent/change-password" : "/parent";
    return <Navigate to={target} replace />;
  }

  // First-login password reset gate for parents
  if (user.role === "parent" && user.must_change_password && !window.location.pathname.startsWith("/parent/change-password")) {
    return <Navigate to="/parent/change-password" replace />;
  }

  if (permission && (user.role === "branch_admin" || user.role === "special_access")) {
    const perms = user.permissions || {};
    const acceptable = Array.isArray(permission) ? permission : [permission];
    const ok = acceptable.some((p) => perms[p]);
    if (!ok) return <Navigate to="/dashboard" replace />;
  }

  return children;
}
